import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Pencil, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import apiClient, { getAuthToken } from '@/utils/api';

const EmployeesPage = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    permissions: ['orders'],
    password: ''
  });

  const PERMISSION_GROUPS = [
    {
      group: 'Dashboard',
      options: [
        { id: 'view_dashboard', label: 'Dashboard Sekmesini Görme' }
      ]
    },
    {
      group: 'Masalar',
      options: [
        { id: 'view_tables', label: 'Masalar Sekmesini Görme' },
        { id: 'manage_table_status', label: 'Masa Açma / Kapatma' },
        { id: 'add_table', label: 'Masa Ekleme' },
        { id: 'delete_table', label: 'Masa Silme' }
      ]
    },
    {
      group: 'Siparişler',
      options: [
        { id: 'view_orders', label: 'Siparişler Sekmesini Görme' },
        { id: 'checkout_orders', label: 'Hesaba Git' },
        { id: 'print_receipt', label: 'Adisyon Yazdır' }
      ]
    },
    {
      group: 'Geçmiş Siparişler',
      options: [
        { id: 'view_past_orders', label: 'Geçmiş Sip. Sekmesini Görme' },
        { id: 'print_past_receipt', label: 'Adisyon Yazdırma' }
      ]
    },
    {
      group: 'Diğer',
      options: [
        { id: 'reservations', label: 'Rezervasyon Yönetimi' },
        { id: 'menu', label: 'Menü Yönetimi' },
        { id: 'revenue', label: 'Gelir Raporu' },
        { id: 'employees', label: 'Çalışan Yönetimi' },
        { id: 'settings', label: 'Ayarlar' }
      ]
    }
  ];

  const getPermissionLabel = (id) => {
    for (const g of PERMISSION_GROUPS) {
      for (const o of g.options) {
        if (o.id === id) return o.label;
      }
    }
    return id;
  };

  useEffect(() => {
    if (!getAuthToken()) {
      navigate('/login');
      return;
    }
    fetchEmployees();
  }, [navigate]);

  const fetchEmployees = async () => {
    try {
      const response = await apiClient.get('/employees');
      setEmployees(response.data);
    } catch (error) {
      toast.error('Çalışanlar yüklenirken hata oluştu');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Ad ve e-posta gerekli');
      return;
    }

    if (!editingEmployee && !formData.password) {
      toast.error('Şifre gerekli');
      return;
    }

    try {
      if (editingEmployee) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          permissions: formData.permissions,
          is_active: true
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await apiClient.put(`/employees/${editingEmployee.id}`, updateData);
        toast.success('Çalışan güncellendi');
      } else {
        await apiClient.post('/employees', formData);
        toast.success('Çalışan eklendi');
      }
      setFormData({ name: '', email: '', phone: '', permissions: ['orders'], password: '' });
      setEditingEmployee(null);
      setShowDialog(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    
    let perms = employee.permissions || [];
    const expandedPerms = new Set(perms);
    if (perms.includes('tables')) {
      expandedPerms.add('view_tables').add('manage_table_status').add('add_table').add('delete_table');
      expandedPerms.delete('tables');
    }
    if (perms.includes('orders')) {
      expandedPerms.add('view_orders').add('checkout_orders').add('print_receipt').add('view_past_orders').add('print_past_receipt');
      expandedPerms.delete('orders');
    }
    
    setFormData({
      name: employee.name,
      email: employee.email || '',
      phone: employee.phone || '',
      permissions: Array.from(expandedPerms),
      password: ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm('Bu çalışanı silmek istediğinize emin misiniz?')) return;

    try {
      await apiClient.delete(`/employees/${employeeId}`);
      toast.success('Çalışan silindi');
      fetchEmployees();
    } catch (error) {
      toast.error('Çalışan silinirken hata oluştu');
    }
  };

  const getPermissionsText = (permissions) => {
    if (!permissions || permissions.length === 0) return 'Yetkisiz';
    if (permissions.includes('all')) return 'Tüm Yetkiler';
    
    const labels = permissions.map(p => getPermissionLabel(p));
    return labels.join(', ');
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Çalışan Yönetimi
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Çalışanları yönetin ve yetkilendirin</p>
          </div>
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) {
              setEditingEmployee(null);
              setFormData({ name: '', email: '', phone: '', permissions: ['orders'], password: '' });
            }
          }}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-employee-button"
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full"
              >
                <UserPlus size={20} className="mr-2" />
                Çalışan Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-slate-900 dark:text-slate-100">
                  {editingEmployee ? 'Çalışanı Düzenle' : 'Yeni Çalışan'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Input
                  data-testid="employee-name-input"
                  placeholder="Ad Soyad"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                />
                <Input
                  data-testid="employee-email-input"
                  placeholder="E-posta (opsiyonel)"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                />
                <Input
                  data-testid="employee-phone-input"
                  placeholder="Telefon (opsiyonel)"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                />
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Yetkiler</p>
                  <div className="space-y-4 max-h-60 overflow-y-auto pr-2">
                    {PERMISSION_GROUPS.map((group, gIdx) => (
                      <div key={gIdx} className="space-y-2">
                        <p className="text-xs font-bold text-slate-500 uppercase">{group.group}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {group.options.map((opt) => (
                            <div key={opt.id} className="flex items-center space-x-2">
                              <Checkbox 
                                id={`perm-${opt.id}`}
                                checked={formData.permissions.includes(opt.id) || formData.permissions.includes('all')}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setFormData({...formData, permissions: [...formData.permissions, opt.id]});
                                  } else {
                                    setFormData({...formData, permissions: formData.permissions.filter(p => p !== opt.id && p !== 'all')});
                                  }
                                }}
                              />
                              <label 
                                htmlFor={`perm-${opt.id}`}
                                className="text-xs text-slate-700 dark:text-slate-300 cursor-pointer"
                              >
                                {opt.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center space-x-2 mt-4 pt-2 border-t border-slate-200 dark:border-slate-800">
                      <Checkbox 
                        id="perm-all"
                        checked={formData.permissions.includes('all')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({...formData, permissions: ['all']});
                          } else {
                            setFormData({...formData, permissions: []});
                          }
                        }}
                      />
                      <label 
                        htmlFor="perm-all"
                        className="text-sm font-bold text-orange-400 cursor-pointer"
                      >
                        Tüm Yetkiler (Tam Erişim)
                      </label>
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  <Input
                    data-testid="employee-password-input"
                    type="password"
                    placeholder={editingEmployee ? "Yeni Şifre (Değiştirmek istemiyorsanız boş bırakın)" : "Şifre"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                  />
                  {editingEmployee && (
                    <p className="text-xs text-slate-500">Çalışanın şifresini sıfırlamak için buraya yeni bir şifre girin.</p>
                  )}
                </div>
                <Button
                  data-testid="save-employee"
                  onClick={handleSubmit}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                >
                  {editingEmployee ? 'Güncelle' : 'Ekle'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {employees.map((employee) => (
            <Card
              key={employee.id}
              data-testid={`employee-${employee.id}`}
              className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-6 hover:border-orange-500/30 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{employee.name}</h3>
                  <p className="text-orange-400 text-sm mt-1">{getPermissionsText(employee.permissions)}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs ${
                  employee.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {employee.is_active ? 'Aktif' : 'Pasif'}
                </span>
              </div>
              <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400 mb-4">
                {employee.email && <p>📧 {employee.email}</p>}
                {employee.phone && <p>📱 {employee.phone}</p>}
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid={`edit-employee-${employee.id}`}
                  onClick={() => handleEdit(employee)}
                  className="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:bg-slate-700"
                  size="sm"
                >
                  <Pencil size={16} className="mr-1" />
                  Düzenle
                </Button>
                <Button
                  data-testid={`delete-employee-${employee.id}`}
                  onClick={() => handleDelete(employee.id)}
                  variant="destructive"
                  size="sm"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {employees.length === 0 && (
          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-12 text-center">
            <UserPlus size={48} className="mx-auto text-slate-600 mb-4" />
            <p className="text-slate-600 dark:text-slate-400">Henüz çalışan eklenmemiş</p>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default EmployeesPage;